import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Grid,
  Tooltip,
  AlertTitle,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Replay as RetryIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as ViewIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import knowledgeBaseService, {
  KnowledgeBase,
  KnowledgeDocument,
  KnowledgeTask,
} from '@/services/knowledgeBaseService';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_started':
        return 'default';
      case 'pending_update':
        return 'warning';
      case 'building':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return {
    backgroundColor: theme.palette[getStatusColor(status)].main,
    color: theme.palette[getStatusColor(status)].contrastText,
    fontWeight: 'bold',
  };
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const KnowledgeBaseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [tasks, setTasks] = useState<KnowledgeTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // 获取知识库详情
  const fetchKnowledgeBaseDetail = async () => {
    if (!id) return;

    try {
      const response = await knowledgeBaseService.getKnowledgeBase(parseInt(id));
      setKnowledgeBase(response.data);
    } catch (error: any) {
      console.error('获取知识库详情失败:', error);
      setSnackbar({ open: true, message: '获取知识库详情失败', severity: 'error' });
    }
  };

  // 获取文档列表
  const fetchDocuments = async () => {
    if (!id) return;

    try {
      const response = await knowledgeBaseService.getKnowledgeBaseDocuments(parseInt(id));
      setDocuments(response.data.documents);
    } catch (error: any) {
      console.error('获取文档列表失败:', error);
    }
  };

  // 获取任务列表
  const fetchTasks = async () => {
    if (!id) return;

    try {
      const response = await knowledgeBaseService.getKnowledgeBaseTasks(parseInt(id));
      setTasks(response.data.tasks);
    } catch (error: any) {
      console.error('获取任务列表失败:', error);
    }
  };

  // 初始化数据
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      await Promise.all([
        fetchKnowledgeBaseDetail(),
        fetchDocuments(),
        fetchTasks(),
      ]);
      setLoading(false);
    };

    initData();

    // 监听全局文档刷新事件
    const handleGlobalRefresh = () => {
      console.log('Global document refresh event received in KnowledgeBaseDetailPage');
      fetchDocuments();
    };

    window.addEventListener('documentsShouldRefresh', handleGlobalRefresh);

    return () => {
      window.removeEventListener('documentsShouldRefresh', handleGlobalRefresh);
    };
  }, [id]);

  // 处理文档上传
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    // 验证文件类型
    const allowedTypes = [
      '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.md'
    ];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(fileExtension)) {
      setSnackbar({
        open: true,
        message: '不支持的文件类型，请上传 PDF、Word、Excel、PowerPoint 或文本文件',
        severity: 'error',
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      await knowledgeBaseService.uploadDocument(
        parseInt(id),
        file,
        (progress) => setUploadProgress(progress)
      );

      setSnackbar({
        open: true,
        message: '文档上传成功，正在后台处理',
        severity: 'success',
      });

      // 刷新数据
      await Promise.all([fetchDocuments(), fetchTasks()]);

      // 刷新知识库详情
      await fetchKnowledgeBaseDetail();

    } catch (error: any) {
      console.error('文档上传失败:', error);
      setSnackbar({
        open: true,
        message: '文档上传失败',
        severity: 'error',
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }

    // 清空文件输入
    if (event.target) {
      event.target.value = '';
    }
  };

  // 创建手动任务
  const handleCreateManualTask = async (taskType: 'document_conversion' | 'dataset_reconstruction') => {
    if (!id) return;

    try {
      await knowledgeBaseService.createManualTask(parseInt(id), taskType);
      setSnackbar({
        open: true,
        message: `手动${taskType === 'dataset_reconstruction' ? '重构数据集' : '转换文档'}任务已创建`,
        severity: 'success',
      });
      await fetchTasks();
    } catch (error: any) {
      console.error('创建手动任务失败:', error);
      setSnackbar({
        open: true,
        message: '创建手动任务失败',
        severity: 'error',
      });
    }
  };

  // 重试任务
  const handleRetryTask = async (taskId: number) => {
    try {
      await knowledgeBaseService.retryTask(taskId);
      setSnackbar({
        open: true,
        message: '任务已重新提交',
        severity: 'success',
      });
      await fetchTasks();
    } catch (error: any) {
      console.error('重试任务失败:', error);
      setSnackbar({
        open: true,
        message: '重试任务失败',
        severity: 'error',
      });
    }
  };

  // 获取状态文本
  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      not_started: '未开始',
      pending_update: '待更新',
      building: '构建中',
      completed: '已完成',
      failed: '失败',
      pending: '待处理',
      processing: '处理中',
      new: '新建',
    };
    return statusMap[status] || status;
  };

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'not_started':
        return <HourglassEmptyIcon />;
      case 'pending_update':
        return <UpdateIcon />;
      case 'building':
        return <RefreshIcon className="animate-spin" />;
      case 'completed':
        return <CheckCircleIcon />;
      case 'failed':
        return <ErrorIcon />;
      case 'pending':
        return <HourglassEmptyIcon />;
      case 'processing':
        return <RefreshIcon className="animate-spin" />;
      default:
        return null;
    }
  };

  // 格式化时间
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  // 格式化文件大小
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          加载中...
        </Typography>
      </Box>
    );
  }

  if (!knowledgeBase) {
    return (
      <Box p={3}>
        <Alert severity="error">
          <AlertTitle>错误</AlertTitle>
          知识库不存在或已被删除
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* 页面头部 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/knowledge-base')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {knowledgeBase.name}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => Promise.all([fetchKnowledgeBaseDetail(), fetchDocuments(), fetchTasks()])}
            sx={{ mr: 2 }}
          >
            刷新
          </Button>
        </Box>
      </Box>

      {/* 知识库信息卡片 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            知识库信息
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                数据集名称
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {knowledgeBase.dataset_name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                构建状态
              </Typography>
              <StatusChip
                label={getStatusText(knowledgeBase.build_status)}
                status={knowledgeBase.build_status}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                文档数量
              </Typography>
              <Typography variant="body1">
                {knowledgeBase.document_count}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                启用状态
              </Typography>
              <Chip
                label={knowledgeBase.is_enabled ? '启用' : '未启用'}
                color={knowledgeBase.is_enabled ? 'success' : 'default'}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                描述
              </Typography>
              <Typography variant="body1">
                {knowledgeBase.description || '暂无描述'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 标签页 */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="文档管理" />
          <Tab label="任务管理" />
        </Tabs>
      </Box>

      {/* 文档管理标签页 */}
      <TabPanel value={currentTab} index={0}>
        <Card>
          <CardContent>
            {/* 上传区域 */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                上传文档
              </Typography>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                    borderColor: '#999',
                  },
                }}
              >
                <input
                  type="file"
                  id="document-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
                <label htmlFor="document-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={uploading}
                  >
                    {uploading ? '上传中...' : '选择文件'}
                  </Button>
                </label>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  支持的文件格式：PDF、Word、Excel、PowerPoint、TXT、MD
                </Typography>

                {uploading && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      上传进度: {uploadProgress}%
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* 手动操作按钮 */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                手动操作
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => handleCreateManualTask('dataset_reconstruction')}
                sx={{ mr: 2 }}
              >
                重构数据集
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* 文档列表 */}
            <Typography variant="h6" gutterBottom>
              文档列表 ({documents.length})
            </Typography>
            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>文件名</TableCell>
                    <TableCell>文件大小</TableCell>
                    <TableCell>转换状态</TableCell>
                    <TableCell>Cognee状态</TableCell>
                    <TableCell>上传时间</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          暂无文档
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    documents.map((doc) => (
                      <StyledTableRow key={doc.id}>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {doc.original_filename}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatFileSize(doc.file_size)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(doc.conversion_status)}
                            <StatusChip
                              label={getStatusText(doc.conversion_status)}
                              status={doc.conversion_status}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(doc.cognee_status)}
                            <StatusChip
                              label={getStatusText(doc.cognee_status)}
                              status={doc.cognee_status}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(doc.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="重试转换">
                            <IconButton
                              size="small"
                              onClick={() => doc.id && handleCreateManualTask('document_conversion', doc.id)}
                              disabled={doc.conversion_status === 'processing'}
                            >
                              <RetryIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* 任务管理标签页 */}
      <TabPanel value={currentTab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              任务列表 ({tasks.length})
            </Typography>
            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>任务类型</TableCell>
                    <TableCell>状态</TableCell>
                    <TableCell>进度</TableCell>
                    <TableCell>创建时间</TableCell>
                    <TableCell>开始时间</TableCell>
                    <TableCell>完成时间</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          暂无任务
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tasks.map((task) => (
                      <StyledTableRow key={task.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {task.task_type === 'document_conversion' ? '文档转换' : '数据集重构'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(task.status)}
                            <StatusChip
                              label={getStatusText(task.status)}
                              status={task.status}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ width: 100 }}>
                            <LinearProgress variant="determinate" value={task.progress} />
                            <Typography variant="caption">{task.progress}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(task.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {task.started_at ? formatDateTime(task.started_at) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {task.completed_at ? formatDateTime(task.completed_at) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="重试任务">
                            <IconButton
                              size="small"
                              onClick={() => handleRetryTask(task.id)}
                              disabled={task.status === 'processing' || task.status === 'pending'}
                            >
                              <RetryIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KnowledgeBaseDetailPage;